import {UIIconProps} from '@dropbox/dig-icons';
import {
  AnalyticsLine,
  AppDrawerLine,
  CalendarLine,
  ChecklistLine,
  ClockLine,
  HeartLine,
  JiraExternalLogo,
  NewsLine,
  PersonMultipleLine,
  TargetLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {displayName} from 'views/apps/appUtils';

import {DashboardState, TileId, TileSpan} from './dashboardModel';
import {appSlugFromTileId} from './pinnedApps';
import {getSelectableQuarters} from './tiles/earnings/earningsData';

/** Employee user_id allowed to see Meet Someone and Jira tiles. */
export const DASHBOARD_PREVIEW_TILES_USER_ID = '14444';

export const canAccessPreviewTiles = (userId?: string): boolean =>
  userId === DASHBOARD_PREVIEW_TILES_USER_ID;

export type LiveSectionId = 'today' | 'work' | 'team';

export type TileContentStatus = 'loading' | 'present' | 'empty';

export interface LiveDashboardContext {
  /** Viewer belongs to a team with a slug (drives Team calendar tile). */
  hasTeam: boolean;
}

export interface LiveTileBehavior {
  /** Logical grouping for tile ordering (today / work / team). */
  section: LiveSectionId;
  /** Whether the tile has content, is still loading, or is empty for this viewer. */
  status: (state: DashboardState, ctx: LiveDashboardContext) => TileContentStatus;
  /**
   * Keep the tile off the board by default, even when it has content. The viewer
   * can still opt in by adding it in customize mode (it shows in the add tray).
   */
  defaultHidden?: boolean;
  /** Only the preview user sees this tile in the customize "add" tray. */
  previewUserOnly?: boolean;
}

export interface TileMeta {
  labelKey: string;
  icon: UIIconProps['src'];
  /** Default grid footprint ({cols} × {rows}). */
  defaultSpan: TileSpan;
  /**
   * Curated footprints the user can pick for this tile, ordered smallest →
   * largest. Each one is a layout that looks good for the tile's content (rather
   * than exposing every width × height combination). Omit for tiles that
   * shouldn't be resizable — they stay locked to {@link defaultSpan}.
   */
  sizes?: TileSpan[];
}

type TileCatalogEntry = TileMeta & {
  id: TileId;
  live?: LiveTileBehavior;
};

/** `present` when there's content, otherwise `empty`. */
const presence = (hasContent: boolean): TileContentStatus => (hasContent ? 'present' : 'empty');

/** `loading` until the query settles, then `present`/`empty` based on content. */
const settled = (loading: boolean | undefined, hasContent: boolean): TileContentStatus =>
  loading ? 'loading' : presence(hasContent);

// Named footprints for readable preset lists ({cols} × {rows}).
const SMALL: TileSpan = {cols: 1, rows: 1};
const TALL: TileSpan = {cols: 1, rows: 2};
const TALLER: TileSpan = {cols: 1, rows: 3};
const XTALLER: TileSpan = {cols: 1, rows: 4};
const LARGE: TileSpan = {cols: 2, rows: 2};
const BIG: TileSpan = {cols: 2, rows: 4};

/** Single registry for dashboard tiles. Array order is live-mode display order. */
const TILE_ENTRIES: TileCatalogEntry[] = [
  {
    id: 'workstreams',
    labelKey: 'dashboard_tile_workstreams',
    icon: WorkstreamsLine,
    defaultSpan: LARGE,
    sizes: [TALL, XTALLER, LARGE, BIG],
    live: {
      section: 'work',
      status: (s) => settled(s.workstreamsLoading, Boolean(s.hasWorkstreams)),
    },
  },
  {
    id: 'earnings',
    labelKey: 'dashboard_tile_earnings',
    icon: AnalyticsLine,
    // Same height as the default Workstreams tile so the two sit side by side.
    defaultSpan: TALL,
    sizes: [TALL, TALLER],
    live: {
      section: 'team',
      status: () => presence(getSelectableQuarters().length > 0),
    },
  },
  {
    id: 'companyFeedback',
    labelKey: 'dashboard_tile_company_feedback',
    icon: ChecklistLine,
    // Give the custom feedback design enough vertical room for its writing field.
    defaultSpan: TALLER,
    sizes: [TALL, TALLER],
    live: {
      section: 'team',
      status: () => 'present',
    },
  },
  {
    id: 'agenda',
    labelKey: 'agenda',
    icon: ChecklistLine,
    defaultSpan: TALLER,
    sizes: [TALL, TALLER, XTALLER, LARGE, BIG],
    live: {section: 'today', status: (s) => settled(s.agendaLoading, s.agenda.length > 0)},
  },
  {
    id: 'teamTimezones',
    labelKey: 'dashboard_tile_timezones',
    icon: ClockLine,
    defaultSpan: TALL,
    live: {
      section: 'team',
      status: (s) => settled(s.timezonesLoading, s.timezoneGroups.length > 0),
    },
  },
  {
    id: 'calendar',
    labelKey: 'dashboard_tile_calendar',
    icon: CalendarLine,
    defaultSpan: TALL,
    sizes: [TALL, BIG],
    live: {
      section: 'team',
      status: (_s, ctx) => presence(ctx.hasTeam),
    },
  },
  {
    id: 'news',
    labelKey: 'dashboard_tile_news',
    icon: NewsLine,
    defaultSpan: TALLER,
    sizes: [TALL, TALLER, XTALLER],
    live: {section: 'team', status: (s) => settled(s.newsLoading, s.news.length > 0)},
  },
  {
    id: 'newArrivals',
    labelKey: 'dashboard_tile_new_arrivals',
    icon: PersonMultipleLine,
    defaultSpan: TALL,
    sizes: [TALL, TALLER, XTALLER],
    live: {
      section: 'team',
      status: (s) => settled(s.newArrivalsLoading, s.newArrivals.length > 0),
    },
  },
  {
    id: 'gratitude',
    labelKey: 'dashboard_tile_gratitude',
    icon: HeartLine,
    defaultSpan: TALL,
    sizes: [SMALL, TALL],
    live: {
      section: 'team',
      status: (s) => settled(s.gratitudeLoading, Boolean(s.latestGratitude)),
      defaultHidden: true,
    },
  },
  {
    id: 'stock',
    labelKey: 'dashboard_tile_stock',
    icon: TargetLine,
    defaultSpan: SMALL,
    live: {
      section: 'team',
      status: (s) => settled(s.stockLoading, Boolean(s.hasStockData)),
    },
  },
  {
    id: 'meetSomeone',
    labelKey: 'dashboard_tile_coffee_chat',
    icon: PersonMultipleLine,
    defaultSpan: TALL,
    sizes: [TALL, LARGE],
    live: {
      section: 'team',
      status: (s) => settled(s.meetSomeoneLoading, Boolean(s.featuredPerson)),
      defaultHidden: true,
      previewUserOnly: true,
    },
  },
  {
    id: 'jira',
    labelKey: 'dashboard_tile_jira',
    icon: JiraExternalLogo,
    defaultSpan: LARGE,
    sizes: [TALL, XTALLER, LARGE, BIG],
    live: {
      section: 'work',
      status: (s) => settled(s.jiraLoading, s.jiraTickets.length > 0),
      defaultHidden: true,
    },
  },
];

export interface LiveTileConfig {
  id: TileId;
  section: LiveSectionId;
  status: (state: DashboardState, ctx: LiveDashboardContext) => TileContentStatus;
  defaultHidden?: boolean;
  previewUserOnly?: boolean;
}

export const LIVE_TILES: LiveTileConfig[] = TILE_ENTRIES.flatMap((entry) =>
  entry.live
    ? [
        {
          id: entry.id,
          section: entry.live.section,
          status: entry.live.status,
          defaultHidden: entry.live.defaultHidden,
          previewUserOnly: entry.live.previewUserOnly,
        },
      ]
    : []
);

/** Global display order for live-mode tiles. */
export const LIVE_TILE_ORDER: TileId[] = LIVE_TILES.map((tile) => tile.id);

/** Tiles that stay off the board until the viewer explicitly adds them. */
export const LIVE_TILE_DEFAULT_HIDDEN: ReadonlySet<TileId> = new Set(
  LIVE_TILES.filter((tile) => tile.defaultHidden).map((tile) => tile.id)
);

/** Tiles only the preview user can add from the customize tray. */
export const LIVE_TILE_PREVIEW_USER_ONLY: ReadonlySet<TileId> = new Set(
  LIVE_TILES.filter((tile) => tile.previewUserOnly).map((tile) => tile.id)
);

export const TILE_CATALOG: Partial<Record<TileId, TileMeta>> = Object.fromEntries(
  TILE_ENTRIES.map(({id, labelKey, icon, defaultSpan, sizes}) => [
    id,
    {labelKey, icon, defaultSpan, sizes},
  ])
) as Partial<Record<TileId, TileMeta>>;

/** Sizes people can pick for an embedded app tile. */
export const APP_TILE_SIZES: TileSpan[] = [TALL, XTALLER, LARGE, BIG];

/** Embedded apps default to a big footprint; the app owns how it fits. */
const APP_TILE_META: Omit<TileMeta, 'labelKey'> = {
  icon: AppDrawerLine,
  defaultSpan: BIG,
  sizes: APP_TILE_SIZES,
};

export const getTileMeta = (tileId: TileId): TileMeta => {
  const appSlug = appSlugFromTileId(tileId);
  if (appSlug) {
    return {...APP_TILE_META, labelKey: displayName(appSlug)};
  }
  const meta = TILE_CATALOG[tileId];
  if (meta) {
    return meta;
  }
  return {
    labelKey: tileId,
    icon: CalendarLine,
    defaultSpan: SMALL,
  };
};

/** Resolved display label for a tile (for headers, buttons, aria-labels). */
export const getTileLabel = (tileId: TileId): string => {
  const appSlug = appSlugFromTileId(tileId);
  if (appSlug) {
    return displayName(appSlug);
  }
  const {labelKey} = getTileMeta(tileId);
  return t(labelKey).toString();
};

/**
 * Curated footprints a tile offers, smallest → largest. Tiles without an
 * explicit preset list aren't resizable and return just their default span.
 */
export const getTileSizes = (tileId: TileId): TileSpan[] => {
  const meta = getTileMeta(tileId);
  if (meta.sizes && meta.sizes.length > 0) {
    return meta.sizes;
  }
  return [meta.defaultSpan];
};

/** Tiles a user can add in customize mode. Preview-only tiles require the preview user. */
export const getAddableTileIds = (userId?: string): TileId[] =>
  LIVE_TILE_ORDER.filter(
    (id) => canAccessPreviewTiles(userId) || !LIVE_TILE_PREVIEW_USER_ONLY.has(id)
  );
